import {
  ActionIcon,
  Badge,
  Box,
  Button,
  FileButton,
  Group,
  Image,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import {
  ExchangeActions,
  type ExchangeActionsExchange,
} from '../../components/ExchangeActions';
import { MessageBubble } from '../../components/MessageBubble';
import { ReportButton } from '../../components/ReportButton';
import { SystemMessage, type SystemMessageKind } from '../../components/SystemMessage';
import { EmptyState, MotionSection } from '../../components/ui';
import { csrfHeaders } from '../../lib/auth-context';
import { track } from '../../lib/telemetry';
import { showToast } from '../../lib/toast';
import { connectThread, type ThreadEvent } from '../../lib/ws-client';

/**
 * /inbox/:threadId — message stream + composer.
 *
 * Realtime: opens a ThreadDO WebSocket on mount; on any inbound `message_*`
 * event the React Query cache is invalidated so the next render pulls fresh
 * server data (the canonical source — DO is fanout-only).
 *
 * Optimistic send: the user sees their bubble immediately; the POST response
 * either confirms the id or surfaces the error and the optimistic row is
 * dropped on next refetch.
 */

/**
 * Local 1.6-stroke line icons — house style mirrors the inbox/index.tsx
 * SearchIcon and the brand LineIcons kit. 24x24 viewBox, fill none,
 * stroke currentColor, rounded caps/joins. Decorative (aria-hidden); buttons
 * keep their own aria-labels. Defined locally per codebase convention — no
 * new shared module.
 */
type IconProps = { size?: number };

function SendIcon({ size = 18 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable={false}
    >
      <path d="M22 2 11 13" />
      <path d="M22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function PaperclipIcon({ size = 18 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable={false}
    >
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

function CloseIcon({ size = 14 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable={false}
    >
      <path d="M18 6 6 18" />
      <path d="M6 6l12 12" />
    </svg>
  );
}

function ChevronLeftIcon({ size = 18 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable={false}
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function MusicNoteIcon({ size = 16 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable={false}
    >
      <path d="M9 18V5l12-2v13" />
      <circle cx={6} cy={18} r={3} />
      <circle cx={18} cy={16} r={3} />
    </svg>
  );
}

function FileIcon({ size = 16 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable={false}
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

function ExternalLinkIcon({ size = 13 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable={false}
      style={{ marginLeft: 3, verticalAlign: '-1px' }}
    >
      <path d="M14 4h6v6" />
      <path d="M20 4 11 13" />
      <path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
    </svg>
  );
}

// Elevation token (SOFT) for the message-stream box. Each layer is a complete
// light-dark() pair so it recolours per scheme; layers separated by top-level
// commas. See the inbox design contract.
const SOFT_ELEVATION =
  'light-dark(0 1px 2px rgba(33,28,22,0.04), 0 1px 2px rgba(0,0,0,0.40)), light-dark(0 6px 18px rgba(33,28,22,0.05), 0 6px 18px rgba(0,0,0,0.26))';

type ThreadCounterparty =
  | { kind: 'venue'; name: string; slug: string; venueId: string }
  | { kind: 'artist'; name: string; slug: string | null; userId: string };

type ThreadHeader = {
  id: string;
  exchangeId: string;
  exchangeState: ExchangeActionsExchange['state'];
  startDate: string;
  endDate: string;
  lastMessageAt: number;
  counterparty: ThreadCounterparty;
  unreadCount: number;
  viewerRole: 'artist' | 'host';
  // End-of-life context for the composer-disabled ribbon. All nullable; the
  // server only sets the timestamp matching the terminal state.
  decidedAt: number | null;
  cancelledAt: number | null;
  closedAt: number | null;
  cancellationInitiator: 'artist' | 'host' | 'system' | null;
  cancellationReason: string | null;
};

type ThreadAttachment = {
  kind: 'image' | 'file';
  url: string;
  filename: string;
  sizeBytes: number;
  mime: string;
};

type ThreadMessage = {
  id: string;
  threadId: string;
  kind: 'user' | 'system_event';
  body: string;
  fromSelf: boolean;
  authorUserId: string | null;
  createdAt: number;
  editedAt: number | null;
  systemEventKind: SystemMessageKind | null;
  systemEventPayload: Record<string, unknown> | null;
  readByArtistAt: number | null;
  readByHostAt: number | null;
  attachments?: ThreadAttachment[] | null;
};

// Mirror server-side limits — see worker/routes/messages.ts (Wave 4K).
const MAX_ATTACHMENTS_PER_MESSAGE = 4;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const ALLOWED_ATTACHMENT_ACCEPT =
  'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf,audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,audio/aac';

type ThreadResponse = {
  thread: ThreadHeader;
  messages: ThreadMessage[];
  nextCursor: string | null;
};

async function fetchThread(threadId: string): Promise<ThreadResponse> {
  const res = await fetch(`/api/threads/${encodeURIComponent(threadId)}`, {
    credentials: 'include',
    headers: { accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`thread fetch failed: ${res.status}`);
  return (await res.json()) as ThreadResponse;
}

type SendArgs = { body: string; clientRequestId: string; files: File[] };
async function sendMessage(threadId: string, { body, clientRequestId, files }: SendArgs) {
  // Use multipart only when attachments are present — keeps the fast path
  // identical to before for plain-text messages.
  const url = `/api/threads/${encodeURIComponent(threadId)}/messages`;
  const init: RequestInit = {
    method: 'POST',
    credentials: 'include',
    headers: { ...csrfHeaders(), accept: 'application/json' },
  };
  if (files.length > 0) {
    const form = new FormData();
    form.set('body', body);
    form.set('client_request_id', clientRequestId);
    for (const file of files) form.append('files[]', file, file.name);
    init.body = form;
    // NOTE: do NOT set Content-Type — the browser fills in the boundary.
  } else {
    (init.headers as Record<string, string>)['Content-Type'] = 'application/json';
    init.body = JSON.stringify({ body, client_request_id: clientRequestId });
  }
  const res = await fetch(url, init);
  if (!res.ok) {
    // Server returns { error: { message } } as JSON for 4xx via Hono — fall
    // back to text if we can't parse.
    const text = await res.text().catch(() => '');
    let message = '';
    try {
      const parsed = JSON.parse(text) as { error?: { message?: string }; message?: string };
      message = parsed.error?.message ?? parsed.message ?? '';
    } catch {
      message = text;
    }
    throw new Error(message.length > 0 ? message : `send failed: ${res.status}`);
  }
  return (await res.json()) as {
    message: ThreadMessage;
    nudgeInserted: boolean;
    clientRequestId: string | null;
  };
}

async function markMessageRead(threadId: string, messageId: string) {
  await fetch(
    `/api/threads/${encodeURIComponent(threadId)}/messages/${encodeURIComponent(messageId)}/read`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { accept: 'application/json', ...csrfHeaders() },
    },
  );
}

function ThreadDetail() {
  const { threadId } = useParams({ from: '/inbox/$threadId' });
  return <ThreadView threadId={threadId} />;
}

/**
 * ThreadView — the body of `/inbox/:threadId` factored out so the inbox
 * desktop two-pane (`/inbox` ≥ lg) can render it inline without navigating.
 *
 * `embedded` skips the AppShell + Container wrapper (the embedding surface
 * already provides those) and skips the back-arrow row (selecting a
 * different thread is the "back" affordance).
 */
export function ThreadView({
  threadId,
  embedded = false,
}: {
  threadId: string;
  embedded?: boolean;
}) {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['thread', threadId] as const, [threadId]);

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => fetchThread(threadId),
  });

  // Open the WebSocket once per threadId.
  useEffect(() => {
    const client = connectThread(threadId);
    const off = client.subscribe((ev: ThreadEvent) => {
      switch (ev.type) {
        case 'message_posted':
        case 'system_message_posted':
        case 'message_edited':
        case 'message_read':
        case 'state_changed':
          // The DO is fanout-only; refetch from the canonical source.
          void queryClient.invalidateQueries({ queryKey });
          // Also refresh the inbox list + the exchanges list (preview, unread,
          // and state badge all changed). Embedding ThreadView on
          // /exchanges/:id means a state transition mid-thread also needs to
          // refresh the exchange detail query so the action strip / banners
          // re-evaluate without a manual reload.
          void queryClient.invalidateQueries({ queryKey: ['inbox'] });
          void queryClient.invalidateQueries({ queryKey: ['exchanges', 'list'] });
          void queryClient.invalidateQueries({ queryKey: ['exchange'] });
          break;
        case 'connected':
          // After (re)connect, refetch in case we missed events while down.
          void queryClient.invalidateQueries({ queryKey });
          break;
        case 'disconnected':
        case 'typing':
        case 'pong':
        case 'thread_read':
          break;
      }
    });
    return () => {
      off();
      client.close();
    };
  }, [threadId, queryClient, queryKey]);

  // On mount + on data load, mark unread incoming messages as read.
  useEffect(() => {
    if (data === undefined) return;
    const incomingUnread = data.messages.filter((m) => {
      if (m.kind !== 'user') return false;
      if (m.fromSelf) return false;
      return data.thread.viewerRole === 'artist'
        ? m.readByArtistAt === null
        : m.readByHostAt === null;
    });
    if (incomingUnread.length === 0) return;
    void Promise.all(incomingUnread.map((m) => markMessageRead(threadId, m.id))).then(() => {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: ['inbox'] });
    });
  }, [data, threadId, queryClient, queryKey]);

  if (isLoading) {
    const loader = (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
    if (embedded) return loader;
    return <AppShell tone="content">{loader}</AppShell>;
  }
  if (error !== null && error !== undefined) {
    const empty = (
      <EmptyState
        title="We couldn't load this thread."
        body="It may have moved, or there's a network blip. Head back and try again."
        action={
          <Button component={Link} to="/inbox" variant="subtle">
            Back to inbox
          </Button>
        }
      />
    );
    if (embedded) return empty;
    return <AppShell tone="content">{empty}</AppShell>;
  }
  if (data === undefined) return null;

  const counterpartyName = data.thread.counterparty.name;
  const artistDisplayName =
    data.thread.counterparty.kind === 'artist'
      ? counterpartyName
      : data.thread.viewerRole === 'artist'
        ? 'you'
        : counterpartyName;

  const composerDisabled =
    data.thread.exchangeState === 'closed' ||
    data.thread.exchangeState === 'cancelled' ||
    data.thread.exchangeState === 'declined';

  // Pin the report subject to the first user message in the thread, not the
  // last. The first message is stable: once chosen, it doesn't shift as new
  // messages arrive, so the modal's "report this message" target maps to a
  // single auditable row even if the user lingers on the page while the
  // counterparty replies. We deliberately also skip the report affordance
  // when the only sender so far is the viewer themselves — the server would
  // reject as a self-report, no point teasing it.
  const firstReportableMessage = data.messages.find(
    (m) => m.kind === 'user' && !m.fromSelf && m.authorUserId !== null,
  );

  const body = (
    <MotionSection>
      <Stack gap="md">
        <Group gap="xs" align="center">
          {embedded ? null : (
            <ActionIcon
              component={Link}
              to="/inbox"
              variant="subtle"
              aria-label="Back to inbox"
              hiddenFrom="lg"
            >
              <ChevronLeftIcon />
            </ActionIcon>
          )}
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Title order={3} style={{ letterSpacing: '-0.01em' }}>
              {/* §6 — clickable counterparty name -> artist profile (the slug is
                  already in the thread payload). Falls back to plain text for
                  venues or when no slug is present. */}
              {data.thread.counterparty.kind === 'artist' &&
              data.thread.counterparty.slug !== null ? (
                <Link
                  to="/artists/$slug"
                  params={{ slug: data.thread.counterparty.slug }}
                  style={{ color: 'var(--mantine-color-brand-7)', textDecoration: 'none' }}
                >
                  {counterpartyName}
                </Link>
              ) : (
                counterpartyName
              )}
            </Title>
            <Group gap={6} wrap="wrap">
              <Text size="sm" c="ink.6">
                {data.thread.startDate}
                {data.thread.endDate !== data.thread.startDate
                  ? ` – ${data.thread.endDate}`
                  : ''}
              </Text>
              <Badge size="sm" variant="light" tt="capitalize" color="brand">
                {data.thread.exchangeState}
              </Badge>
              {embedded ? null : (
                <Link
                  to="/exchanges/$id"
                  params={{ id: data.thread.exchangeId }}
                  style={{
                    fontSize: 13,
                    color: 'var(--mantine-color-brand-7)',
                    textDecoration: 'underline',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  View full details
                  <ExternalLinkIcon />
                </Link>
              )}
            </Group>
          </Stack>
          {firstReportableMessage !== undefined ? (
            <ReportButton
              subjectType="message"
              subjectId={firstReportableMessage.id}
              subjectLabel={counterpartyName}
              variant="icon"
            />
          ) : null}
        </Group>

        {/* Parity actions: the thread view and the exchange detail page share
            one action vocabulary so users never need to leave the
            conversation to confirm/decline/cancel/complete. */}
        <ExchangeActions
          exchange={{
            id: data.thread.exchangeId,
            state: data.thread.exchangeState,
          }}
          viewer={{ role: data.thread.viewerRole }}
          artistDisplayName={artistDisplayName}
          counterpartyName={counterpartyName}
          compact
          onAfterAction={async () => {
            await queryClient.invalidateQueries({ queryKey });
            await queryClient.invalidateQueries({ queryKey: ['inbox'] });
            await queryClient.invalidateQueries({ queryKey: ['exchanges', 'list'] });
            await queryClient.invalidateQueries({ queryKey: ['exchange'] });
          }}
        />

        <Box
          style={{
            border: '1px solid var(--mantine-color-ink-1)',
            borderRadius: 16,
            padding: 20,
            boxShadow: SOFT_ELEVATION,
            background:
              'linear-gradient(180deg, light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-7)) 0%, light-dark(#ffffff, var(--mantine-color-dark-7)) 25%)',
          }}
        >
          <ScrollArea.Autosize mah={520}>
            <Stack gap={0}>
              {data.messages.length === 0 && (
                <Text c="ink.6" size="sm" ta="center" py="md">
                  No messages yet. Say hello below.
                </Text>
              )}
              {data.messages.map((m) =>
                m.kind === 'system_event' ? (
                  <SystemMessage
                    key={m.id}
                    body={m.body}
                    kind={m.systemEventKind}
                    createdAt={m.createdAt}
                  />
                ) : (
                  <Stack key={m.id} gap={4} align={m.fromSelf ? 'flex-end' : 'flex-start'}>
                    <MessageBubble
                      body={m.body}
                      fromSelf={m.fromSelf}
                      authorLabel={m.fromSelf ? 'You' : counterpartyName}
                      createdAt={m.createdAt}
                      editedAt={m.editedAt}
                      readByCounterparty={
                        m.fromSelf
                          ? data.thread.viewerRole === 'artist'
                            ? m.readByHostAt !== null
                            : m.readByArtistAt !== null
                          : false
                      }
                    />
                    {m.attachments && m.attachments.length > 0 ? (
                      <AttachmentList attachments={m.attachments} fromSelf={m.fromSelf} />
                    ) : null}
                  </Stack>
                ),
              )}
            </Stack>
          </ScrollArea.Autosize>
        </Box>

        {composerDisabled ? (
          <ClosedThreadRibbon thread={data.thread} />
        ) : (
          <Composer threadId={threadId} counterpartyName={counterpartyName} disabled={false} />
        )}
      </Stack>
    </MotionSection>
  );

  if (embedded) return body;

  return <AppShell tone="content">{body}</AppShell>;
}

/**
 * ClosedThreadRibbon — replaces the silent composer when the exchange state
 * makes composing impossible. Surfaces the initiator + the terminal date so
 * the reader sees WHY the thread closed, not just THAT it did. CTAs render
 * only where the state machine accepts a follow-up (review / dispute / cancel
 * within the grace window — review/dispute eventually).
 *
 * For now: explanatory copy only. Adding the review / dispute deep-link
 * follow-ups is a one-line change once the wording lands with the user.
 */
function ClosedThreadRibbon({ thread }: { thread: ThreadHeader }) {
  const { exchangeState, cancellationInitiator, cancelledAt, closedAt, decidedAt } = thread;

  const dateLabel = (() => {
    if (exchangeState === 'cancelled' && cancelledAt !== null) return formatRibbonDate(cancelledAt);
    if (exchangeState === 'declined' && decidedAt !== null) return formatRibbonDate(decidedAt);
    if (exchangeState === 'closed' && closedAt !== null) return formatRibbonDate(closedAt);
    return null;
  })();

  const initiatorLabel = (() => {
    if (exchangeState !== 'cancelled') return null;
    if (cancellationInitiator === 'artist') return 'the artist';
    if (cancellationInitiator === 'host') return 'the host';
    if (cancellationInitiator === 'system') return 'WWAAVE';
    return null;
  })();

  const headline = (() => {
    if (exchangeState === 'cancelled') {
      const who = initiatorLabel !== null ? `by ${initiatorLabel}` : '';
      return `Cancelled ${who}${dateLabel !== null ? ` on ${dateLabel}` : ''}.`.replace(/\s+/g, ' ').trim();
    }
    if (exchangeState === 'declined') {
      return `Declined by the host${dateLabel !== null ? ` on ${dateLabel}` : ''}.`;
    }
    if (exchangeState === 'closed') {
      return `Closed${dateLabel !== null ? ` on ${dateLabel}` : ''}. No further changes.`;
    }
    return 'This thread is closed.';
  })();

  return (
    <Box
      py="md"
      px="md"
      style={{
        textAlign: 'center',
        borderTop: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
        background: 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))',
        borderRadius: 12,
      }}
    >
      <Text size="sm" c="ink.7" fw={500}>
        {headline}
      </Text>
      {thread.cancellationReason !== null && thread.cancellationReason.length > 0 ? (
        <Text size="xs" c="dimmed" mt={4} style={{ whiteSpace: 'pre-wrap' }}>
          “{thread.cancellationReason}”
        </Text>
      ) : null}
    </Box>
  );
}

function formatRibbonDate(ms: number): string {
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

function Composer({
  threadId,
  counterpartyName,
  disabled,
}: {
  threadId: string;
  counterpartyName: string;
  disabled: boolean;
}) {
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const queryClient = useQueryClient();
  const inflightId = useRef<string>('');
  const fileInputResetRef = useRef<() => void>(() => undefined);

  const mutation = useMutation({
    mutationFn: ({ body: b, files: fs }: { body: string; files: File[] }) => {
      const cid = crypto.randomUUID();
      inflightId.current = cid;
      return sendMessage(threadId, { body: b, clientRequestId: cid, files: fs });
    },
    onSuccess: (_data, variables) => {
      track('thread.message_sent', {
        chars: variables.body.length,
        attachments: variables.files.length,
      });
      if (variables.files.length > 0) {
        const totalBytes = variables.files.reduce((acc, f) => acc + f.size, 0);
        const bytesBucket: 'small' | 'med' | 'large' =
          totalBytes <= 100_000 ? 'small' : totalBytes <= 1_000_000 ? 'med' : 'large';
        track('thread.attachment_uploaded', {
          count: variables.files.length,
          totalBytesBucket: bytesBucket,
        });
      }
      setBody('');
      setFiles([]);
      fileInputResetRef.current();
      void queryClient.invalidateQueries({ queryKey: ['thread', threadId] });
      void queryClient.invalidateQueries({ queryKey: ['inbox'] });
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Could not send your message.';
      track('thread.message_send_failed', {
        reason: classifySendError(message),
      });
      showToast({
        message,
        variant: 'error',
      });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = body.trim();
    if (trimmed.length === 0) return;
    if (mutation.isPending) return;
    mutation.mutate({ body: trimmed, files });
  }

  function addFiles(picked: File[]) {
    if (picked.length === 0) return;
    // Cap to MAX_ATTACHMENTS_PER_MESSAGE in total.
    const room = Math.max(0, MAX_ATTACHMENTS_PER_MESSAGE - files.length);
    if (room === 0) {
      track('thread.attachment_upload_failed', { reason: 'limit' });
      showToast({
        message: `Up to ${MAX_ATTACHMENTS_PER_MESSAGE} files per message.`,
        variant: 'error',
      });
      return;
    }
    const accepted: File[] = [];
    for (const f of picked.slice(0, room)) {
      if (f.size > MAX_ATTACHMENT_BYTES) {
        track('thread.attachment_upload_failed', { reason: 'size' });
        showToast({ message: `${f.name} is over the 10 MB limit.`, variant: 'error' });
        continue;
      }
      accepted.push(f);
    }
    if (accepted.length === 0) return;
    setFiles((prev) => [...prev, ...accepted]);
  }

  function removeFileAt(index: number) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  // ThreadView swaps in a ClosedThreadRibbon when the exchange state forbids
  // composing, so this branch is now a defensive backstop. Kept terse —
  // surfacing the same generic line as before.
  if (disabled) {
    return (
      <Box
        py="md"
        style={{
          textAlign: 'center',
          borderTop: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
        }}
      >
        <Text size="sm" c="dimmed">
          This thread is closed.
        </Text>
      </Box>
    );
  }

  const canAddMore = files.length < MAX_ATTACHMENTS_PER_MESSAGE;

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="xs">
        <Textarea
          placeholder={`Write a message to ${counterpartyName}`}
          value={body}
          onChange={(e) => setBody(e.currentTarget.value)}
          autosize
          minRows={2}
          maxRows={8}
          maxLength={8000}
          disabled={mutation.isPending}
          onKeyDown={(e) => {
            // Enter to send, Shift+Enter for newline.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
        />
        {files.length > 0 && (
          <Group gap="xs" wrap="wrap">
            {files.map((f, i) => (
              <Group
                key={`${f.name}-${i}`}
                gap={4}
                px="xs"
                py={4}
                style={{
                  border: '1px solid var(--mantine-color-ink-1)',
                  borderRadius: 999,
                  background: 'light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-5))',
                  fontSize: 13,
                }}
              >
                <Text size="xs" lineClamp={1} style={{ maxWidth: 180 }}>
                  {f.name}
                </Text>
                <Text size="xs" c="dimmed">
                  {formatBytes(f.size)}
                </Text>
                <ActionIcon
                  variant="subtle"
                  size="xs"
                  aria-label={`Remove ${f.name}`}
                  onClick={() => removeFileAt(i)}
                  disabled={mutation.isPending}
                >
                  <CloseIcon />
                </ActionIcon>
              </Group>
            ))}
          </Group>
        )}
        <Group justify="space-between" align="center">
          <FileButton
            multiple
            accept={ALLOWED_ATTACHMENT_ACCEPT}
            onChange={(picked) => addFiles(picked)}
            resetRef={fileInputResetRef}
            disabled={mutation.isPending || !canAddMore}
          >
            {(props) => (
              <Tooltip
                label={
                  canAddMore
                    ? `Attach files (up to ${MAX_ATTACHMENTS_PER_MESSAGE - files.length} more)`
                    : 'Attachment limit reached'
                }
              >
                <ActionIcon
                  {...props}
                  variant="subtle"
                  aria-label="Attach files"
                  disabled={mutation.isPending || !canAddMore}
                >
                  <PaperclipIcon />
                </ActionIcon>
              </Tooltip>
            )}
          </FileButton>
          <Button
            type="submit"
            variant="filled"
            color="brand.6"
            rightSection={<SendIcon size={16} />}
            loading={mutation.isPending}
            disabled={body.trim().length === 0}
          >
            Send
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

/**
 * AttachmentList — renders attachments under the message bubble.
 *
 *   - image/* attachments render as small thumbnails. Click → modal full size.
 *   - application/pdf renders as an inline link (browser opens in a new tab).
 *   - audio/* renders as a download link with the original filename.
 */
function AttachmentList({
  attachments,
  fromSelf,
}: {
  attachments: ThreadAttachment[];
  fromSelf: boolean;
}) {
  const [previewIdx, setPreviewIdx] = useState<number | null>(null);
  const align = fromSelf ? 'flex-end' : 'flex-start';

  return (
    <Stack gap={4} align={align} style={{ maxWidth: '80%' }}>
      <Group gap={6} wrap="wrap" justify={fromSelf ? 'flex-end' : 'flex-start'}>
        {attachments.map((att, i) => {
          if (att.kind === 'image' && att.mime.startsWith('image/')) {
            return (
              <Box
                key={`${att.url}-${i}`}
                role="button"
                tabIndex={0}
                aria-label={`Open ${att.filename}`}
                onClick={() => setPreviewIdx(i)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setPreviewIdx(i);
                  }
                }}
                style={{
                  cursor: 'pointer',
                  borderRadius: 8,
                  overflow: 'hidden',
                  border: '1px solid var(--mantine-color-ink-1)',
                  width: 96,
                  height: 96,
                  background: 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-5))',
                }}
              >
                <Image
                  src={att.url}
                  alt={att.filename}
                  w={96}
                  h={96}
                  fit="cover"
                  loading="lazy"
                />
              </Box>
            );
          }
          // Non-image: anchor to the proxied URL. Audio downloads (server sets
          // Content-Disposition: attachment); pdf opens inline.
          const isAudio = att.mime.startsWith('audio/');
          return (
            <Box
              key={`${att.url}-${i}`}
              component="a"
              href={att.url}
              target="_blank"
              rel="noopener noreferrer"
              {...(isAudio ? { download: att.filename } : {})}
              style={{
                display: 'inline-flex',
                gap: 6,
                alignItems: 'center',
                padding: '6px 10px',
                border: '1px solid var(--mantine-color-ink-1)',
                borderRadius: 8,
                fontSize: 13,
                color: 'var(--mantine-color-brand-7)',
                textDecoration: 'none',
                background: 'light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-5))',
              }}
            >
              <span aria-hidden style={{ display: 'inline-flex' }}>
                {isAudio ? <MusicNoteIcon /> : <FileIcon />}
              </span>
              <Text size="xs" lineClamp={1} style={{ maxWidth: 200 }}>
                {att.filename}
              </Text>
              <Text size="xs" c="dimmed">
                {formatBytes(att.sizeBytes)}
              </Text>
            </Box>
          );
        })}
      </Group>
      <Modal
        opened={previewIdx !== null}
        onClose={() => setPreviewIdx(null)}
        size="lg"
        centered
        title={previewIdx !== null ? attachments[previewIdx]?.filename : ''}
      >
        {previewIdx !== null && attachments[previewIdx] ? (
          <Image
            src={attachments[previewIdx]!.url}
            alt={attachments[previewIdx]!.filename}
            fit="contain"
            mah="80vh"
          />
        ) : null}
      </Modal>
    </Stack>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function classifySendError(message: string): 'size' | 'type' | 'network' | 'unknown' {
  const m = message.toLowerCase();
  if (m.includes('size') || m.includes('10 mb') || m.includes('too large')) return 'size';
  if (m.includes('mime') || m.includes('type')) return 'type';
  if (m.includes('network') || m.includes('failed to fetch') || m.includes('offline')) {
    return 'network';
  }
  return 'unknown';
}

export const Route = createFileRoute('/inbox/$threadId')({
  component: ThreadDetail,
});
